import { useEffect, useState } from "react";
import api from "../services/api";
import { Loader2, Activity, IndianRupee, TrendingUp, Gauge } from "lucide-react";

const GROUPS = [
  { key: "operational", label: "Operational", icon: Gauge, color: "blue" },
  { key: "financial", label: "Financial", icon: IndianRupee, color: "emerald" },
  { key: "growth", label: "Growth", icon: TrendingUp, color: "violet" },
];

export default function AdminKPIs() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/kpis/")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>;
  if (!data) return <div className="p-10 text-center text-sm text-slate-400">Failed to load KPIs</div>;

  return (
    <div className="space-y-8" data-testid="admin-kpis">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-slate-900">Platform KPIs</h1>
          <p className="text-sm text-slate-500 mt-0.5">Weekly operational, financial &amp; growth metrics (Playbook)</p>
        </div>
        <p className="text-xs text-slate-400">Updated: {new Date(data.generated_at).toLocaleString()}</p>
      </div>

      {GROUPS.map((g) => (
        <section key={g.key} data-testid={`kpi-group-${g.key}`}>
          <div className="flex items-center gap-2 mb-4">
            <div className={`h-8 w-8 rounded-lg flex items-center justify-center bg-${g.color}-100`}><g.icon className={`h-4 w-4 text-${g.color}-600`} /></div>
            <h2 className="font-['Outfit'] text-lg font-bold text-slate-900">{g.label}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(data[g.key] || []).map((kpi) => <KpiCard key={kpi.key} kpi={kpi} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

function KpiCard({ kpi }) {
  const val = kpi.value ?? 0;
  const target = kpi.target;
  const lowerBetter = kpi.lower_is_better;
  let hitTarget = null;
  if (target !== undefined) {
    hitTarget = lowerBetter ? val <= target : val >= target;
  }
  const color = hitTarget === null ? "slate" : hitTarget ? "emerald" : "amber";

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={`kpi-${kpi.key}`}>
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{kpi.label}</p>
      <div className="flex items-baseline gap-1">
        <p className="font-['Outfit'] text-3xl font-bold text-slate-900">
          {kpi.unit === "₹" ? "₹" : ""}{typeof val === "number" ? val.toLocaleString("en-IN") : val}
        </p>
        {kpi.unit && kpi.unit !== "₹" && <span className="text-sm text-slate-400">{kpi.unit}</span>}
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs">
        {target !== undefined && (
          <span className={`px-2 py-0.5 rounded-full font-semibold bg-${color}-50 text-${color}-700 border border-${color}-200`}>
            Target: {lowerBetter ? "≤" : "≥"} {target}{kpi.unit !== "₹" ? kpi.unit : ""}
          </span>
        )}
        {kpi.hint && <span className="text-slate-400 text-[11px]">{kpi.hint}</span>}
      </div>
    </div>
  );
}
